import React from "react";
import { Helmet } from "react-helmet";
import shop from "../assets/shop.jpg";
import { PageShell } from "./ui/PageShell";
import { Eyebrow } from "./ui/Eyebrow";
import insta from "../assets/instalogo.png";
import face from "../assets/facelogo.png";
import linked from "../assets/linked.png";
import { FaEnvelope, FaPhone, FaMapMarkerAlt } from "react-icons/fa";
import CallButton from "./ui/CallButton";
import DirectionsButton from "./ui/DirectionsButton";
import { buttonVariants } from "./ui/Buttons";
import { cn } from "../lib/cn";
import { MAPS_DIRECTIONS_URL } from "../lib/businessInfo";
import BusinessHours from "./ui/BusinessHours";
import ReviewSection from "./ui/ReviewSection";
import TrustBadges from "./ui/TrustBadges";
import VehicleBrandGrid from "./ui/VehicleBrandGrid";
import WarrantyNotice from "./ui/WarrantyNotice";
import ServiceAreas from "./ui/ServiceAreas";

const contactLinkClass =
  "flex min-h-[48px] items-start gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm transition duration-200 ease-brand hover:border-teal-600 touch-manipulation";

const iconWrapClass =
  "mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-teal-600/25 bg-teal-50";

const Contact = () => {
  return (
    <>
      {/* ---------- SEO META ---------- */}
      <Helmet>
        <title>Contact Advanced Automotive Smog | Temecula Auto Repair Shop</title>
        <meta
          name="description"
          content="Contact Advanced Automotive Smog in Temecula to schedule repair, diagnostics, or a smog check. Call, email, or get directions to visit the shop."
        />
        <meta
          name="keywords"
          content="contact Advanced Automotive Smog, Temecula auto repair shop, schedule auto repair Temecula, smog check appointment Temecula"
        />
        <meta
          property="og:title"
          content="Contact Advanced Automotive Smog | Temecula Auto Repair Shop"
        />
        <meta
          property="og:description"
          content="Contact Advanced Automotive Smog in Temecula to schedule repair, diagnostics, or a smog check. Call, email, or get directions to visit the shop."
        />
        <meta
          property="og:url"
          content="https://www.advancedautomotivesmog.com/contact"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://www.advancedautomotivesmog.com/assets/contact.jpeg"
        />
        <link
          rel="canonical"
          href="https://www.advancedautomotivesmog.com/contact"
        />
        <meta name="robots" content="index, follow" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Contact Advanced Automotive Smog | Temecula Auto Repair Shop"
        />
        <meta
          name="twitter:description"
          content="Contact Advanced Automotive Smog in Temecula to schedule repair, diagnostics, or a smog check. Call, email, or get directions to visit the shop."
        />
        <meta
          name="twitter:image"
          content="https://www.advancedautomotivesmog.com/assets/contact.jpeg"
        />
      </Helmet>

      <PageShell className="bg-[#F8F9FA] text-slate-700">
        {/* Intro */}
        <div className="border-b border-slate-200 bg-[#EEF2F7]">
        <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 pb-10 pt-7 sm:px-5 md:px-8 md:pt-8">
          <div className="mb-0 max-w-3xl text-center md:text-left">
            <Eyebrow tone="light" className="mb-4">
              Advanced Automotive · Contact
            </Eyebrow>
            <h1 className="mb-4 text-[1.65rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl md:text-4xl">
              Get in touch with{" "}
              <span className="text-teal-600">Advanced Automotive Smog</span>.
            </h1>
            <p className="text-sm font-normal leading-relaxed text-slate-500 md:text-base">
              Call, email, or visit the shop in Temecula. Whether it&apos;s a
              quick smog check or diagnostics for a high-end vehicle, we&apos;re
              here to help.
            </p>

            <TrustBadges tone="light" className="mt-10 text-left" />
            <VehicleBrandGrid tone="light" className="mt-8 text-left" />
            <WarrantyNotice tone="light" className="mt-6 max-w-xl text-left" />
          </div>
        </div>
        </div>

        {/* Shop photo + contact card */}
        <div className="border-t border-slate-200 bg-white py-10 md:py-14">
          <div className="mx-auto flex max-w-6xl flex-col items-stretch gap-8 border-l-4 border-slate-300 px-4 sm:px-5 md:px-8 lg:flex-row lg:gap-10">
            <div className="w-full lg:w-1/2">
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition duration-300 ease-brand">
                <img
                  src={shop}
                  alt="Advanced Automotive Smog shop exterior"
                  className="h-56 w-full object-cover object-center transition duration-500 ease-brand sm:h-72 md:h-80 md:hover:scale-[1.02]"
                />
              </div>
            </div>

            <div className="w-full lg:w-1/2">
              <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7 md:p-8">
                <h2 className="mb-6 text-center text-xl font-semibold tracking-tight text-navy-900 sm:text-2xl md:text-left">
                  Contact information
                </h2>

                <div className="space-y-5">
                  <a
                    href={MAPS_DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-gtm="directions-click"
                    className={cn(contactLinkClass, "directions-button")}
                  >
                    <div className={iconWrapClass}>
                      <FaMapMarkerAlt size={18} className="text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-navy-900 sm:text-base">
                        Address
                      </h3>
                      <p className="text-xs leading-snug text-slate-500 sm:text-sm">
                        42103 Rio Nedo, Ste 108
                        <br />
                        Temecula, CA 92590
                      </p>
                    </div>
                  </a>

                  <a
                    href="tel:+19512961242"
                    data-gtm="phone-click"
                    className={contactLinkClass}
                  >
                    <div className={iconWrapClass}>
                      <FaPhone size={18} className="text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-navy-900 sm:text-base">
                        Phone
                      </h3>
                      <p className="text-xs leading-snug text-slate-500 sm:text-sm">
                        (951) 296-1242
                      </p>
                    </div>
                  </a>

                  <a
                    href="mailto:advancedautomotivesmog@gmail.com"
                    className={contactLinkClass}
                  >
                    <div className={iconWrapClass}>
                      <FaEnvelope size={18} className="text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-navy-900 sm:text-base">
                        Email
                      </h3>
                      <p className="break-all text-xs leading-snug text-slate-500 sm:break-normal sm:text-sm">
                        advancedautomotivesmog@gmail.com
                      </p>
                    </div>
                  </a>
                </div>

                <div className="mt-7 border-t border-slate-200 pt-5">
                  <p className="mb-3 text-center text-xs text-slate-500 sm:text-sm md:text-left">
                    Follow us for updates, tips, and behind-the-scenes from the
                    shop:
                  </p>
                  <div className="flex justify-center gap-5 md:justify-start">
                    <a
                      href="https://www.facebook.com/profile.php?id=61573424138548"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className="transition-transform duration-200 hover:scale-110"
                    >
                      <img
                        src={face}
                        alt="Facebook"
                        className="h-8 w-8 sm:h-9 sm:w-9"
                      />
                    </a>
                    <a
                      href="https://www.instagram.com/advautosmog/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className="transition-transform duration-200 hover:scale-110"
                    >
                      <img
                        src={insta}
                        alt="Instagram"
                        className="h-8 w-8 sm:h-9 sm:w-9"
                      />
                    </a>
                    <a
                      href="https://www.linkedin.com/in/edgar-depaz-3ba172353/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn"
                      className="transition-transform duration-200 hover:scale-110"
                    >
                      <img
                        src={linked}
                        alt="LinkedIn"
                        className="h-8 w-9 sm:h-9 sm:w-10"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 bg-[#F8F9FA]">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 py-10 sm:px-5 md:px-8">
          <ServiceAreas
            tone="light"
            className="mb-10 mt-0 max-w-4xl md:mx-0 md:max-w-none"
          />

          <div className="mb-2 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:justify-center">
            <CallButton className="px-8 py-3 text-base" />
            <DirectionsButton className="px-8 py-3 text-base" />
          </div>
          </div>
        </div>

        <div className="border-t border-slate-200 bg-[#EEF2F7] py-12 md:py-14">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
            <BusinessHours tone="light" className="mb-10" />
            <ReviewSection tone="light" className="mb-0" />
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white pb-16 pt-10">
          <div className="mx-auto max-w-6xl border-l-4 border-slate-300 px-4 sm:px-5 md:px-8">
          <h2 className="mb-4 text-center text-2xl font-semibold text-navy-900 sm:text-3xl">
            Find Us Here
          </h2>
          <p className="mx-auto mb-5 max-w-2xl text-center text-xs text-slate-500 sm:text-sm">
            We&apos;re located just off Rio Nedo in Temecula. Use{" "}
            <span className="font-medium text-slate-600">Get Directions</span>{" "}
            to open Google Maps in a new tab, or explore the map below.
          </p>
          <div className="mb-6 flex justify-center px-1">
            <a
              href={MAPS_DIRECTIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-gtm="directions-click"
              className={cn(
                buttonVariants.primary,
                "directions-button w-full max-w-md px-6 text-center sm:w-auto sm:min-w-[12.5rem]",
              )}
            >
              Get Directions
            </a>
          </div>
          <div className="h-[min(50vh,17.5rem)] min-h-[14rem] w-full max-w-full overflow-hidden rounded-xl border border-slate-200 bg-slate-50 shadow-sm sm:h-80 md:h-96">
            <iframe
              title="Map of Advanced Automotive Smog at 42103 Rio Nedo, Temecula"
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d12698.358178399632!2d-117.129025!3d33.533298!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80db7a447cc2cd1b%3A0x8b828a4840d7f2d5!2s42103%20Rio%20Nedo%20Ste%20108%2C%20Temecula%2C%20CA%2092590!5e0!3m2!1sen!2sus!4v1698082820607!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
          </div>
        </div>
      </PageShell>
    </>
  );
};

export default Contact;
